/**
 * Action classes and rungs — the authority vocabulary (D7, O1 item 6)
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * What Marva may do, as data. Not what she should do — that is judgment.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 *
 * The merchant used to answer "may Marva act?" through six per-campaign booleans with
 * thresholds — `campaigns.priceDrop.enabled`, `minDropPercent`, `lapsedThresholdDays` and
 * the rest. That is the merchant programming the assistant, which is the seam the product
 * exists to remove (`docs/brand/product-tests.md` test 3), and it is why adding a
 * touchpoint used to cost a settings section.
 *
 * It is replaced by one rung over these classes. The merchant sees the rung and a
 * plain-language list of what it covers; the classes are internal until a merchant asks to
 * narrow one, at which point they become a named downward exception carrying a bound.
 *
 * ── Why this module is dependency-free ──────────────────────────────────────────────────
 *
 * Same reason as `./block-reasons`: `schemas.js` needs these strings to describe a table,
 * and `governance-enforcer` builds a DynamoDB client at load. Importing the enforcer from
 * the schema module once meant describing a table instantiated an AWS client everywhere
 * (memory: `reference_schemas_js_must_stay_dependency_free`). Keep this file pure data.
 *
 * @module action-classes
 */

/**
 * The rungs, in ascending order of what Marva may do without asking.
 *
 * These are the product's three, not the four-step `observe → approve → delegated →
 * broader` in the original spec — settled by JP, with the model underneath (classes,
 * downward exceptions with bounds, the three zones) following the spec unchanged.
 *
 * ⚠️ `shadow` is ALSO mirrored onto `workspace.mode`, because ~30 readers and several
 * billing paths key off `mode === 'shadow'` literally. The rung is canonical; the mirror
 * is a migration artefact, written server-side only. See `docs/work/settings-consolidation.md`.
 */
const RUNGS = Object.freeze(['shadow', 'assisted', 'autonomous']);

/** Index for "does this rung cover that requirement" comparisons. Never compare by string. */
const rungRank = (rung) => RUNGS.indexOf(rung);

/**
 * Action classes, and the lowest rung that covers each.
 *
 * `needs` is the rung at which Marva may do this at all. A merchant exception can only ever
 * narrow that further (downward, never up) — §6.5 option B.
 *
 * ⚠️ There is no `bounded` marker here yet. Whether an exception can carry a *number* rather
 * than being a plain on/off only matters once PER-EXCEPTION bounds exist, and phase 1″ did not
 * bring them: it added the CEILING zone, whose number is the workspace's own
 * `discountGovernance.maxDiscountPercent` and reaches the enforcer as `proposal.discountCeiling`.
 * `AuthorityGrantSchema.kind` is still `z.literal('off')`. A downward bound on a single
 * exception is phase 1‴. A field nothing reads is the configured-but-inert shape CLAUDE.md warns
 * about; it arrives with the code that uses it.
 */
const ACTION_CLASSES = Object.freeze({
  answer_grounded: { needs: 'assisted', label: 'Answers a question she can source' },
  recommend: { needs: 'assisted', label: 'Recommends a product' },
  ask_merchant: { needs: 'assisted', label: 'Brings you what she cannot stand behind' },
  offer_shipping_waiver: { needs: 'assisted', label: 'Waives delivery' },
  offer_discount_within_band: { needs: 'assisted', label: 'Discounts inside your band' },
  originate_followup: { needs: 'assisted', label: 'Starts a conversation you did not ask for' },
  // Named in the spec as future classes. Declared so an exception or a decision can reference
  // one without a schema change, but nothing routes to them yet.
  initiate_return: { needs: 'autonomous', label: 'Starts a return' },
  initiate_exchange: { needs: 'autonomous', label: 'Starts an exchange' },
  add_to_cart: { needs: 'autonomous', label: 'Puts something in a cart' }
});

const ACTION_CLASS_IDS = Object.freeze(Object.keys(ACTION_CLASSES));

/**
 * Which action class an outbound trigger belongs to.
 *
 * These keys were the legacy gateway `TRIGGER_CONFIG_MAP`'s keys (deleted by MAR-112)
 * (`services/engagement/engagement-gateway/src/index.js`), which were already de-facto
 * action classes — each one had a boolean gating it. The mapping is what lets the rung
 * replace those booleans without inventing a second taxonomy. One key is not: `order_delivered`
 * (MAR-423) never had a boolean, so authority is the only merchant boundary on it.
 *
 * A trigger absent from this map is NOT gated by authority. That is deliberate and matches
 * the gate it replaces: chat, exit-intent, confusion, dwell, comparison and cart abandonment
 * are ungated today ("always on when the workspace is active"), because they are a reply to
 * something the shopper just did, not an outbound Marva originated.
 */
const TRIGGER_ACTION_CLASS = Object.freeze({
  browse_timeout: 'originate_followup',
  back_in_stock: 'originate_followup',
  price_drop: 'originate_followup',
  open_loop_due: 'ask_merchant',
  merchant_answer_received: 'ask_merchant',
  // MAR-423: a post-delivery check-in is a conversation Marva starts — the merchant's existing
  // `originate_followup` ceiling governs it, and no new setting does. It has no legacy flag (it
  // never had a campaign switch), which `TRIGGERS_WITHOUT_LEGACY_FLAG` in authority-derivation
  // declares, so the cutover's drift guards still catch a trigger added here by omission.
  order_delivered: 'originate_followup',
  // MAR-425: a memory-change rematch is a recommendation Marva starts — the same
  // `originate_followup` ceiling, no new setting, and no legacy flag (TRIGGERS_WITHOUT_LEGACY_FLAG).
  memory_rematch: 'originate_followup'
});

/**
 * The classes the enforcer may resolve from a decision's PROPOSAL rather than its trigger.
 *
 * `resolveActionClasses` derives the two offer classes from their concrete proposal fields.
 * `ask_merchant` is different: a trusted server-side caller names it explicitly after the
 * agent has selected that tool. It is still listed here because it is a proposal-routed class,
 * not authority inferred from a trigger name.
 */
const PROPOSAL_ACTION_CLASSES = Object.freeze([
  'ask_merchant',
  'offer_discount_within_band',
  'offer_shipping_waiver'
]);

/**
 * Classes a trusted caller may name directly as `proposal.actionClass`.
 *
 * Offer authority is derived from the offer itself; accepting a naked explicit offer class
 * would let a caller obtain an authority verdict for an act it did not actually propose.
 * New direct classes must be deliberately added here and centrally tested. Unknown and merely
 * declared-but-unbuilt classes are refused by the enforcer rather than ignored.
 */
const EXPLICIT_PROPOSAL_ACTION_CLASSES = Object.freeze(['ask_merchant']);

/**
 * The classes anything can actually ASK FOR today — and therefore the only ones a rung can
 * make a difference to.
 *
 * ⚠️ Read this before saying what a rung "means" to a merchant. `resolveActionClasses` in the
 * enforcer is the sole producer of a class on the send path, and it can emit exactly these:
 * the trigger map's values, plus the proposal classes above. Every one of them
 * `needs: 'assisted'` — so
 * `autonomous` currently grants **nothing beyond `assisted`**, and `initiate_return`,
 * `initiate_exchange` and `add_to_cart` are declared but unreachable: no caller ever asks for
 * them, at any rung.
 *
 * A surface that renders `rungCovers()` alone will therefore tell a merchant Marva "starts
 * returns" the moment they pick `autonomous`, which is false. Coverage is `rungCovers` AND
 * routed. `governance-enforcer.test.js` asserts `resolveActionClasses` can never emit a class
 * outside this set, so the two cannot drift.
 *
 * ⚠️ **Unrouted does not mean she does not do it.** The five unrouted classes are two different
 * kinds, and a surface must not merge them:
 *
 *   - `answer_grounded`, `recommend` — she DOES these, constantly, in chat. Authority does not
 *     gate them **by design**: they are replies to something the shopper just did, and the
 *     `TRIGGER_ACTION_CLASS` docblock above explains why those stay ungated. No rung turns them
 *     on or off, so a rung-coverage list must not claim credit for them.
 *   - `initiate_return`, `initiate_exchange`, `add_to_cart` — she cannot do these at all. No
 *     caller requests them at any rung.
 *
 * The discriminator, without a hardcoded list: an unrouted class needing MORE than
 * `HIGHEST_EFFECTIVE_RUNG` is unbuilt; one at or below it is live but ungated. Asserted in
 * `governance-enforcer.test.js` so surfaces may rely on it.
 */
const ROUTED_ACTION_CLASSES = Object.freeze(
  [...new Set([...Object.values(TRIGGER_ACTION_CLASS), ...PROPOSAL_ACTION_CLASSES])]
);

/** Can anything actually request this class today? See `ROUTED_ACTION_CLASSES`. */
function isRoutedActionClass(actionClass) {
  return ROUTED_ACTION_CLASSES.includes(actionClass);
}

/**
 * The highest rung that currently changes anything, and why a surface should care.
 *
 * Every routed class needs `assisted`, so a merchant moving from `assisted` to `autonomous`
 * changes no verdict the enforcer reaches. Offering it as a capability upgrade sells behaviour
 * the platform does not have. Computed rather than hardcoded: the day a routed class needs
 * `autonomous`, this moves on its own.
 */
const HIGHEST_EFFECTIVE_RUNG = ROUTED_ACTION_CLASSES.reduce(
  (highest, id) => (
    rungRank(ACTION_CLASSES[id].needs) > rungRank(highest) ? ACTION_CLASSES[id].needs : highest
  ),
  RUNGS[0]
);

/** True when `rung` is high enough for `actionClass`. Unknown class → not covered. */
function rungCovers(rung, actionClass) {
  const cls = ACTION_CLASSES[actionClass];
  if (!cls) return false;
  const at = rungRank(rung);
  if (at === -1) return false;
  return at >= rungRank(cls.needs);
}

module.exports = {
  RUNGS,
  rungRank,
  ACTION_CLASSES,
  ACTION_CLASS_IDS,
  TRIGGER_ACTION_CLASS,
  PROPOSAL_ACTION_CLASSES,
  EXPLICIT_PROPOSAL_ACTION_CLASSES,
  ROUTED_ACTION_CLASSES,
  HIGHEST_EFFECTIVE_RUNG,
  isRoutedActionClass,
  rungCovers
};
